/* eslint-disable no-unused-vars */
import React, { useEffect, useRef, useState, memo, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';
import _ from 'lodash';

import { RootState, select } from 'src/configs/store';
import SportBet from '../../SportBet/SportBet';
import NumberBet from '../../NumberBet/NumberBet';
import RacingBet from '../../RacingBet/RacingBet';
import PermutationBet from '../../PermutationBet/PermutationBet';
import Icon from 'src/components/Icon/Icon';
import useIsMobile from 'src/hooks/useIsMobile';
import { isMobile } from 'react-device-detect';

interface BetsListProps {
  showCombinations: boolean,
  permutationsRef: React.MutableRefObject<any>,
  totalCombinationsWithStake: number,
  showBanker: boolean
}

function BetsList({ permutationsRef, totalCombinationsWithStake, showBanker }: BetsListProps) {
  const { t } = useTranslation('common');
  const [openCombinations, setOpenCombinations] = useState([]);
  const [showPermutations, setShowPermutations] = useState(false);

  const stakes = useSelector((state: RootState) => state.betslip.stakes);
  const bets = useSelector((state: RootState) => state.betslip.bets);
  const slip = useSelector((state: RootState) => state.betslip.slip);
  const selectedCombinations = useSelector((state: RootState) => state.betslip.systemSlip.selectedCombinations);
  const processing = useSelector((state: RootState) => state.betslip.processing);
  const systemSlip = useSelector((state: RootState) => state.betslip.systemSlip);
  const isGetTicket = useSelector((state: RootState) => state.betslip.isGetTicket);
  const divisor = useSelector((state: RootState) => state.betslip.divisor);
  const showProfit = useSelector((state: RootState) => state.betslip.showProfit);
  const newBetIndex = useSelector((state: RootState) => state.betslip.newBetIndex);
  const secondScreen = useSelector((state: RootState) => state.content.secondScreen);

  const stakingRules = useSelector((state: RootState) => state.content.stakingRules);
  const combinations = useSelector(select.betslip.combinations);
  const selectedSlip = useSelector(select.betslip.selectedSlip);

  const mobile = useIsMobile();
  const dispatch = useDispatch();

  const betsContainer = useRef(null);
  const lastBet = useRef(null);
  const bottom = useRef(null);

  let permutationsIndex = 0;
  let _slip = 'single';
  if (slip === 1) _slip = 'acca';
  else if (slip === 2) _slip = 'system';

  useEffect(() => {
    const highestSelectionIndex = _.findIndex(selectedCombinations, (combination) => combination > _.size(combinations));
    if (slip === 2 && highestSelectionIndex > -1){
      dispatch.betslip.removeSelectedCombination(selectedCombinations[highestSelectionIndex]);
    }
  }, [bets, processing]);

  useEffect(() => {
    const open = openCombinations.length > 0 ? [...Array(_.size(combinations)).keys()] : [];
    setOpenCombinations(open);
    dispatch.betslip.changeReadOpenCombinations(open);
  }, [bets.length]);

  useEffect(() => {
    if (slip === 2 && _.isEmpty(selectedCombinations) && _.size(combinations) > 0 && !processing) {
      dispatch.betslip.addSelectedCombinations(1);
    }
  }, [slip, combinations, processing]);

  useEffect(() => {
    if (systemSlip.stakeSelection === 'differentStake'){
      const newOpenCombinations = [...Array(_.size(combinations)).keys()];
      setOpenCombinations(newOpenCombinations);
      dispatch.betslip.changeReadOpenCombinations(newOpenCombinations);
    }
  }, [systemSlip.stakeSelection]);

  useEffect(() => {
    if (slip === 2 && !isGetTicket){
      let lastIndex = 0;
      let stakeIndex = 1;
      const betStakes = [];

      for (const values of Object.keys(combinations)){
        for (let i=0; i < combinations[values].length; i+=1) {
          const index = lastIndex;
          betStakes.push(!selectedCombinations.includes(stakeIndex) ? { index, value: 0 } :
            { index, value: systemSlip.stakes[index] > 0 ? systemSlip.stakes[index] / divisor : stakingRules.sports.system.default });
          lastIndex+=1;
        }
        stakeIndex+=1;
      }

      dispatch.betslip.updateMultipleSystemStakes(JSON.parse(JSON.stringify(betStakes)));
    }

  }, [selectedCombinations.length, systemSlip.stakes.length, systemSlip.stakeSelection]);

  useEffect(() => {
    if (showPermutations){
      permutationsRef.current.firstElementChild?.scrollIntoView({
        behavior:'smooth',
        block: 'end',
        inline:'nearest'
      });
    }
  }, [showPermutations]);

  const handleCombinationsClick = (e) => {
    e.preventDefault();
    setShowPermutations(!showPermutations);
    if (openCombinations.length === _.size(combinations)){
      setOpenCombinations([]);
    }
    dispatch.betslip.changeReadShowPermutations(!showPermutations);
  };

  const hideProfit = selectedSlip.stakeSelection !== 'differentStake' && !showProfit;

  const handleScroll = () => {
    if (newBetIndex === bets.length -1){
      bottom.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    } else {
      lastBet.current = betsContainer.current.children[newBetIndex];
      lastBet.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  useLayoutEffect(() => {
    if (!isMobile){
      handleScroll();
    }
  }, [newBetIndex]);

  return (
    <div>
      <div ref={ betsContainer } className="bets-list__relative">
        {
          _.map(bets, (bet, index: number) => {
            const showHeader = index === 0 || bets[index - 1].eventCode !== bet.eventCode;
            const showTournament = index === 0 || bets[index-1].tournamentName !== bet.tournamentName;

            switch (bet.type) {
              case 'numbers':
                return (
                  <NumberBet
                    key={ index }
                    stake={ _.find(stakes, s => s.lines.length === 1 && s.lines[0] === (index + 1)) }
                    rule={ stakingRules.lotto[_slip] }
                    bet={ bet }
                    showHeader = { showHeader }
                    showTournament={ showTournament }
                    checkCombinable={ slip !== 0 }
                    index={ index }
                    hideProfit={ hideProfit }
                  >
                    {
                      bet.permutationsCount === 1 && (
                        <NumberBet.SimpleBet
                          index={ index }
                          key={ index }
                          allowStakes={ slip !== 2 && selectedSlip.stakeSelection === 'differentStake' }
                          totalCombinations={ totalCombinationsWithStake }
                          hideProfit={ hideProfit }
                        />
                      )
                    }
                    {
                      bet.permutationsCount > 1 && (
                        <NumberBet.ComboBet
                          index={ index }
                          allowStakes={ slip !== 2 && selectedSlip.stakeSelection === 'differentStake' }
                          totalCombinations={ totalCombinationsWithStake }
                          hideProfit={ hideProfit }

                        />
                      )
                    }
                    {
                      !bet.permutationsCount && (
                        <NumberBet.SpecialBet
                          index={ index }
                          allowStakes={ slip !== 2 && selectedSlip.stakeSelection === 'differentStake' }
                          showBanker={ showBanker }
                          hideProfit={ hideProfit }
                        />
                      )
                    }
                  </NumberBet>
                );

              case 'racing':
                return (
                  <RacingBet
                    key={ index + bet.eventName + bet.marketName + bet.outcome }
                    bet={ bet }
                    index={ index }
                    stake={ _.find(stakes, s => s.lines.length === 1 && s.lines[0] === (index + 1)) }
                    showHeader={ showHeader }
                    showTournament={ showTournament }
                    showBanker={ showBanker }
                    rule={ stakingRules.racing[_slip] || (bet.oddsType === 2 ? stakingRules.racing.tote : stakingRules.racing.fixedOdds) }
                    allowStakes={ slip !== 2 && selectedSlip.stakeSelection === 'differentStake' }
                    checkCombinable={ slip !== 0 }
                    hideProfit={ hideProfit }
                  />
                );

              default:
                return (
                  <SportBet
                    key={ bet.eventName + bet.marketName + bet.outcome }
                    bet={ bet }
                    index={ index }
                    stake={ _.find(stakes, s => s.lines.length === 1 && s.lines[0] === (index + 1)) }
                    rule={ stakingRules.sports[_slip] }
                    showBanker={ showBanker }
                    allowStakes={ slip !== 2 && selectedSlip.stakeSelection === 'differentStake' }
                    showHeader={ showHeader }
                    showTournament={ showTournament }
                    checkCombinable={ slip !== 0 }
                    hideProfit={ hideProfit }
                  />
                );
            }
          })
        }
        <div className="bets-list__bottom" ref={ bottom } />
      </div>
      { /* ANY OF BETS */ }

      <div
        className={
          classNames('slip-form__permutations', {
            'slip-form__permutations--expanded': showPermutations === true
          })
        }
      >
        <div
          className={
            classNames({
              'slip-form__permutations-title': true,
              'slip-form__permutations-title--opened': showPermutations
            })
          }
          onClick= { handleCombinationsClick }
        >
          { `${ t('betslip.combinations') } ${ totalCombinationsWithStake }` }
          <Icon
            icon="arrow"
            className={ classNames('slip-form__dropdown-arrow', { 'slip-form__dropdown-arrow--opened': showPermutations }) }
            onClick={ handleCombinationsClick }
          />
        </div>
        <div ref={ permutationsRef }>
          {
            showPermutations &&
          _.map(Object.keys(combinations), (key, index) => {
            if (index > 0) permutationsIndex += Object.values(combinations)[index - 1].length || 0;

            return (
              <PermutationBet
                key={ key }
                startIndex={ permutationsIndex }
                combination={ combinations[key] }
                index={ index + 1 }
                setOpen= { setOpenCombinations }
                openCombinations={ openCombinations }
                name={ key }
                allowStakes={ slip === 2 && selectedSlip.stakeSelection === 'differentStake' }
              />
            );
          })
          }
        </div>
      </div>
    </div>
  );
}

export default memo(BetsList);
